/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    queryOptions,
    skipToken,
    useQuery,
    type QueryClient,
} from '@tanstack/react-query';

import { getExternalMedia } from './media.api';
import type { ExternalMedia } from './media.types';

export const EXTERNAL_MEDIA_KEY = 'externalMediaUrl';

export function getExternalMediaQueryKey(mediaId: string | undefined) {
    return [EXTERNAL_MEDIA_KEY, mediaId] as const;
}

/**
 * Query options shared by `useGetExternalMediaQuery` and `refreshExternalMedia`, so an
 * imperative refresh writes to the cache exactly like the hook does.
 */
export function externalMediaQueryOptions(mediaId: string | undefined) {
    return queryOptions({
        queryKey: getExternalMediaQueryKey(mediaId),
        queryFn: mediaId
            ? ({ signal }) => getExternalMedia({ mediaId, signal })
            : skipToken,
        retry: 1,
    });
}

/**
 * Get the URL of an external media item. The fetch is skipped while `mediaId` is empty.
 *
 * Refetched on mount, so `initialData` is shown while it revalidates. Call
 * `refreshExternalMedia` (or invalidate `getExternalMediaQueryKey(mediaId)`) after
 * replacing the media to refresh every consumer.
 *
 * The URLs are CloudFront signed URLs (`Expires`, `Signature`, `Key-Pair-Id`), and the
 * CDN enforces them: once `Expires` passes (currently ~15 minutes after signing), the URL
 * returns 403. Stripping the params doesn't help either, since the CDN then looks for
 * signed cookies instead. So every fetch's freshly signed URL replaces the previous one,
 * and cached URLs must be checked with `isMediaUrlExpired` before they are shown.
 */
export function useGetExternalMediaQuery({
    mediaId,
    initialData,
}: {
    mediaId: string | undefined;
    initialData?: () => ExternalMedia | undefined;
}) {
    return useQuery({
        ...externalMediaQueryOptions(mediaId),
        initialData,
        refetchOnWindowFocus: false,
    });
}

/**
 * Fetch an external media item into the query cache, updating every mounted consumer.
 * Use after uploading or replacing the media. Replacing keeps the same media id, so the
 * query key alone won't trigger a refetch.
 */
export function refreshExternalMedia(queryClient: QueryClient, mediaId: string) {
    return queryClient.fetchQuery(externalMediaQueryOptions(mediaId));
}
